import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiWifi, FiPrinter, FiSearch, FiRefreshCw, FiCheck, FiX } = FiIcons;

const PrinterDiscovery = ({ onPrinterSelect, onClose }) => {
  const [scanning, setScanning] = useState(false);
  const [discoveredPrinters, setDiscoveredPrinters] = useState([]);
  const [scanProgress, setScanProgress] = useState(0);

  // Mock printer discovery - in real implementation, this would scan the network
  const scanForPrinters = async () => {
    setScanning(true);
    setScanProgress(0);
    setDiscoveredPrinters([]);

    // Simulate network scanning
    const mockPrinters = [
      {
        id: 'printer_1',
        name: 'Epson TM-T88V',
        ip: '192.168.1.101',
        mac: '00:11:62:12:34:56',
        type: 'thermal',
        port: 9100,
        status: 'online'
      },
      {
        id: 'printer_2',
        name: 'Star TSP143III',
        ip: '192.168.1.102',
        mac: '00:11:62:78:90:AB',
        type: 'thermal',
        port: 9100,
        status: 'online'
      },
      {
        id: 'printer_3',
        name: 'HP LaserJet Pro',
        ip: '192.168.1.103',
        mac: '00:11:62:CD:EF:12',
        type: 'laser',
        port: 9100,
        status: 'busy'
      },
      {
        id: 'printer_4',
        name: 'Canon PIXMA',
        ip: '192.168.1.104',
        mac: '00:11:62:34:56:78',
        type: 'inkjet',
        port: 9100,
        status: 'offline'
      }
    ];

    // Simulate progressive discovery
    for (let i = 0; i <= 100; i += 10) {
      await new Promise(resolve => setTimeout(resolve, 100));
      setScanProgress(i);
      
      // Add printers at different intervals
      if (i === 30) {
        setDiscoveredPrinters([mockPrinters[0]]);
      } else if (i === 50) {
        setDiscoveredPrinters([mockPrinters[0], mockPrinters[1]]);
      } else if (i === 70) {
        setDiscoveredPrinters([mockPrinters[0], mockPrinters[1], mockPrinters[2]]);
      } else if (i === 90) {
        setDiscoveredPrinters(mockPrinters);
      }
    }

    setScanning(false);
  };

  const testPrinterConnection = async (printer) => {
    // Mock connection test
    await new Promise(resolve => setTimeout(resolve, 1000));
    return printer.status === 'online';
  };

  const handleSelectPrinter = (printer) => {
    onPrinterSelect({
      name: printer.name,
      type: printer.type,
      ipAddress: printer.ip,
      port: printer.port,
      description: `Auto-discovered ${printer.type} printer`
    });
    onClose();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online': return 'text-green-600 bg-green-100';
      case 'busy': return 'text-yellow-600 bg-yellow-100';
      case 'offline': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getTypeIcon = (type) => {
    return FiPrinter; // Could be customized based on printer type
  };

  useEffect(() => {
    // Auto-start scanning when component mounts
    scanForPrinters();
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-playfair text-warm-brown">Discover Printers</h2>
            <p className="text-warm-brown/70 text-sm">Scanning network for available printers...</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <SafeIcon icon={FiX} className="text-gray-600 text-lg" />
          </button>
        </div>

        {/* Scanning Progress */}
        {scanning && (
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center gap-3 mb-3">
              <SafeIcon icon={FiSearch} className="text-warm-brown text-lg animate-pulse" />
              <span className="text-warm-brown font-medium">Scanning network...</span>
              <span className="text-warm-brown/70">{scanProgress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-warm-brown h-2 rounded-full transition-all duration-300"
                style={{ width: `${scanProgress}%` }}
              />
            </div>
          </div>
        )}

        {/* Discovered Printers */}
        <div className="p-6 max-h-96 overflow-y-auto">
          {discoveredPrinters.length === 0 && !scanning && (
            <div className="text-center py-8">
              <SafeIcon icon={FiPrinter} className="text-6xl text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No printers found on the network</p>
              <button
                onClick={scanForPrinters}
                className="mt-4 flex items-center gap-2 px-4 py-2 bg-warm-brown text-cream rounded-lg hover:bg-warm-brown/90 transition-colors mx-auto"
              >
                <SafeIcon icon={FiRefreshCw} className="text-sm" />
                Scan Again
              </button>
            </div>
          )}

          <div className="space-y-3">
            {discoveredPrinters.map((printer) => (
              <motion.div
                key={printer.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="border border-gray-200 rounded-lg p-4 hover:border-warm-brown/30 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-cream p-2 rounded-lg">
                      <SafeIcon icon={getTypeIcon(printer.type)} className="text-warm-brown text-xl" />
                    </div>
                    <div>
                      <h3 className="font-medium text-warm-brown">{printer.name}</h3>
                      <div className="text-sm text-gray-600 space-y-1">
                        <p>IP: {printer.ip}</p>
                        <p>Type: {printer.type}</p>
                        <p>Port: {printer.port}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(printer.status)}`}>
                      {printer.status}
                    </span>
                    <button
                      onClick={() => handleSelectPrinter(printer)}
                      disabled={printer.status === 'offline'}
                      className="flex items-center gap-2 px-4 py-2 bg-warm-brown text-cream rounded-lg hover:bg-warm-brown/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <SafeIcon icon={FiCheck} className="text-sm" />
                      Select
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              <p>💡 <strong>Tip:</strong> Make sure printers are powered on and connected to the same network</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={scanForPrinters}
                disabled={scanning}
                className="flex items-center gap-2 px-4 py-2 border border-warm-brown text-warm-brown rounded-lg hover:bg-warm-brown hover:text-cream transition-colors disabled:opacity-50"
              >
                <SafeIcon icon={FiRefreshCw} className={`text-sm ${scanning ? 'animate-spin' : ''}`} />
                {scanning ? 'Scanning...' : 'Scan Again'}
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default PrinterDiscovery;